import React, { useState } from 'react';
import { X, AlertTriangle, Calendar, Clock, User, FileText } from 'lucide-react';

const CancelAppointmentModal = ({ appointment, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');
  const [selectedReason, setSelectedReason] = useState('');

  const commonReasons = [
    'Schedule conflict',
    'Feeling better',
    'Emergency came up',
    'Need to reschedule',
    'Financial reasons',
    'Other',
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalReason = selectedReason === 'Other' ? reason : selectedReason;
    onConfirm(finalReason);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-1">
                Cancel Appointment
              </h3>
              <p className="text-red-100 text-sm">
                This action cannot be undone
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Appointment Details Card */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 mb-6 border border-gray-200">
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <User className="w-5 h-5 text-gray-700" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Doctor</p>
                <p className="font-bold text-gray-900 text-lg">{appointment.doctor}</p>
                {appointment.specialty && (
                  <p className="text-sm text-gray-600">{appointment.specialty}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="flex items-center gap-2 bg-white rounded-lg p-3">
                <Calendar className="w-4 h-4 text-emerald-600" />
                <div>
                  <p className="text-xs text-gray-500">Date</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {new Date(appointment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-lg p-3">
                <Clock className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">Time</p>
                  <p className="text-sm font-semibold text-gray-900">{appointment.time}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 bg-white rounded-lg p-3 mt-3">
              <FileText className="w-4 h-4 text-purple-600" />
              <div>
                <p className="text-xs text-gray-500">Type</p>
                <p className="text-sm font-semibold text-gray-900">{appointment.type}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Reason Selection */}
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Reason for cancellation <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {commonReasons.map((commonReason) => (
                  <label
                    key={commonReason}
                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      selectedReason === commonReason
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={commonReason}
                      checked={selectedReason === commonReason}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      required
                      className="w-5 h-5 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="ml-3 text-gray-900 font-medium">{commonReason}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom Reason */}
            {selectedReason === 'Other' && (
              <div className="animate-in slide-in-from-top duration-200">
                <label className="block text-sm font-bold text-gray-900 mb-2">
                  Please specify your reason
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                  placeholder="Tell us why you need to cancel this appointment..."
                />
              </div>
            )}

            {/* Warning */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900 mb-1">Cancellation Policy</p>
                <p className="text-xs text-amber-700">
                  If eligible, your refund will be processed within 5-7 business days.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
              >
                Keep Appointment
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-500/30"
              >
                Cancel Appointment
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CancelAppointmentModal;
